import BookGrid from "@app/components/book-grid";
import BookService from "@app/services/http/book.service";
import { Images } from "@assets/images";
import { useEffect, useState } from "react";
import SliderPerView from "./components/slider-per-view";
import { Author, Banner, Book, BookByCategory, TypeOfBook } from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useNavigate } from "react-router-dom";
import { BannerAdsPosition, RoutePath, TypeOfBookValue } from "@app/constants";
import SkeletonGrid from "@app/components/skeleton-grid";
import SliderSpaceBetween from "./components/slider-space-between";
import BannerService from "@app/services/http/banner.service";
import FeaturedAuthor from "./components/featured-author";
import ComingSoon from "./components/coming-soon";
import Trending from "./components/trending";
import TopGoodBooks from "./components/top-good-books";
import BannerAdsComponent from "./components/banner-ads";

type MasterData = {
  bookFree: Book[];
  topReBookComment: Book[];
  latestRelease: Book[];
  sliders: Book[];
  bookByCategory: BookByCategory[];
  banners: Banner[];
  authors: Author[];
  comingSoon: Book[];
  trending: Book[];
};

function Home() {
  const [masterData, setMasterData] = useState<MasterData>({
    bookFree: [],
    topReBookComment: [],
    latestRelease: [],
    sliders: [],
    bookByCategory: [],
    banners: [],
    authors: [],
    comingSoon: [],
    trending: [],
  });
  const [loading, setLoading] = useState(true);

  const { abortController } = useCancellationRequest();

  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      BookService.getBooksByTags(abortController.signal),
      BannerService.getList(abortController.signal),
    ])
      .then(([booksByTagsRes, bannersRes]) => {
        setMasterData({
          bookFree: booksByTagsRes.data.bookFree,
          topReBookComment: booksByTagsRes.data.topBookReComment,
          latestRelease: booksByTagsRes.data.latestRelease,
          sliders: booksByTagsRes.data.sliders,
          bookByCategory: booksByTagsRes.data.bookByCategory,
          banners: bannersRes.data.items,
          authors: booksByTagsRes.data.authors,
          comingSoon: booksByTagsRes.data.comingSoon.filter(
            (item) => !!item.thumbnails?.[0]
          ),
          trending: booksByTagsRes.data.trending,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleCategoryClick = (categoryId: number) => {
    navigate(`${RoutePath.BOOK_BY_CATEGORY}?category_id=${categoryId}`);
  };

  const handleDiscoveryClick = (type: TypeOfBook) => {
    navigate(`${RoutePath.BOOK_BY_TYPE}?type=${type}`);
  };

  return (
    <div className="px-[100px] min-h-[calc(100vh-268px)]">
      {!loading ? (
        <>
          <div className="mt-14 px-9">
            <SliderPerView books={masterData.sliders} />
          </div>
          <div>
            {!!masterData.bookFree.length && (
              <div>
                <div className="flex justify-between items-center mt-[57px]">
                  <div className="text-[#171A20] font-bold text-[26px]">
                    Sách Miễn Phí
                  </div>
                  <div
                    className="text-[#38499b] text-sm font-semibold cursor-pointer flex items-center"
                    onClick={() => handleDiscoveryClick(TypeOfBookValue.FREE)}
                  >
                    Khám phá
                    <img
                      className="inline ml-2"
                      src={Images.ArrowRight.default}
                      alt=""
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <BookGrid books={masterData.bookFree} />
                </div>
              </div>
            )}
            {!!masterData.topReBookComment.length && (
              <div>
                <div className="flex justify-between items-center mt-10">
                  <div className="text-[#171A20] font-bold text-[26px]">
                    Top 30 Sách Hay Nên Đọc
                  </div>
                  <div
                    className="text-[#38499b] text-sm font-semibold cursor-pointer flex items-center"
                    onClick={() =>
                      handleDiscoveryClick(TypeOfBookValue.TOP_RECOMMENT)
                    }
                  >
                    Khám phá
                    <img
                      className="inline ml-2"
                      src={Images.ArrowRight.default}
                      alt=""
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <TopGoodBooks books={masterData.topReBookComment} />
                </div>
              </div>
            )}
            <div className="mt-[60px]">
              <SliderSpaceBetween banners={masterData.banners} />
            </div>
            {!!masterData.trending.length && (
              <div className="mt-[60px]">
                <Trending books={masterData.trending} />
              </div>
            )}
            {!!masterData.latestRelease.length && (
              <div>
                <div className="flex justify-between items-center mt-10">
                  <div className="text-[#171A20] font-bold text-[26px]">
                    Mới xuất bản
                  </div>
                  <div
                    className="text-[#38499b] text-sm font-semibold cursor-pointer flex items-center"
                    onClick={() =>
                      handleDiscoveryClick(TypeOfBookValue.LATEST_RELEASE)
                    }
                  >
                    Khám phá
                    <img
                      className="inline ml-2"
                      src={Images.ArrowRight.default}
                      alt=""
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <BookGrid books={masterData.latestRelease} />
                </div>
              </div>
            )}
            {masterData.bookByCategory?.[0]?.items?.length && (
              <div className="mt-10">
                <div className="text-[#4E586C] font-semibold">
                  Sách hay nhất về chủ đề
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-[#171A20] font-bold text-[26px]">
                    {masterData.bookByCategory[0].categoryName}
                  </div>
                  <div
                    className="text-[#38499b] text-sm font-semibold cursor-pointer flex items-center"
                    onClick={() =>
                      handleCategoryClick(
                        masterData.bookByCategory[0].categoryId
                      )
                    }
                  >
                    Khám phá
                    <img
                      className="inline ml-2"
                      src={Images.ArrowRight.default}
                      alt=""
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <BookGrid books={masterData.bookByCategory[0].items} />
                </div>
              </div>
            )}
            {masterData.bookByCategory?.[1]?.items?.length && (
              <div className="mt-10">
                <div className="text-[#4E586C] font-semibold">
                  Những sách kinh điển của
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-[#171A20] font-bold text-[26px]">
                    {masterData.bookByCategory[1].categoryName}
                  </div>
                  <div
                    className="text-[#38499b] text-sm font-semibold cursor-pointer flex items-center"
                    onClick={() =>
                      handleCategoryClick(
                        masterData.bookByCategory[1].categoryId
                      )
                    }
                  >
                    Khám phá
                    <img
                      className="inline ml-2"
                      src={Images.ArrowRight.default}
                      alt=""
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <BookGrid books={masterData.bookByCategory[1].items} />
                </div>
              </div>
            )}
            {masterData.bookByCategory?.[2]?.items?.length && (
              <div className="mt-10">
                <div className="text-[#4E586C] font-semibold">
                  Đầu sách đỉnh cao thuộc về
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-[#171A20] font-bold text-[26px]">
                    {masterData.bookByCategory[2].categoryName}
                  </div>
                  <div
                    className="text-[#38499b] text-sm font-semibold cursor-pointer flex items-center"
                    onClick={() =>
                      handleCategoryClick(
                        masterData.bookByCategory[2].categoryId
                      )
                    }
                  >
                    Khám phá
                    <img
                      className="inline ml-2"
                      src={Images.ArrowRight.default}
                      alt=""
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <BookGrid books={masterData.bookByCategory[2].items} />
                </div>
              </div>
            )}
            {!!masterData.authors.length && (
              <FeaturedAuthor authors={masterData.authors} />
            )}
            <BannerAdsComponent
              className="mt-[60px]"
              position={BannerAdsPosition.HOME_SCREEN}
            />
            <div className="mt-[60px] text-[#171A20] font-[SVN-Gilroy] text-[26px] font-bold">
              Sắp Ra Mắt
            </div>
            <div className="mt-4">
              {!!masterData.comingSoon.length && (
                <ComingSoon books={masterData.comingSoon} />
              )}
            </div>
          </div>
        </>
      ) : (
        <SkeletonGrid />
      )}
    </div>
  );
}

export default Home;
